import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Inbox } from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { getPeerFeedbackDetail } from '../utils/peerFeedbackApi';
import { PeerFeedbackDetail } from '../types/peerFeedback';
import { renderRichText } from '../utils/richText';
import { ActivityTimeline } from '../components/ActivityTimeline';

const QUESTION_LABELS: Record<string, string> = {
  start_doing:    'What is one thing I should start, stop, and continue doing to make our collaboration more effective?',
  stop_doing:     "When I'm managing a high-pressure situation or a complex project, how does my leadership/working style affect the team's momentum?",
  continue_doing: 'What should I do more to help the team succeed?',
};



export function ViewFeedbackPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiCall } = useApiClient();

  const [detail, setDetail] = useState<PeerFeedbackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestId = Number(id);

  useEffect(() => {
    (async () => {
      try {
        const data = await getPeerFeedbackDetail(requestId, apiCall);
        setDetail(data);
      } catch {
        setError('Could not load feedback responses.');
      } finally {
        setLoading(false);
      }
    })();
  }, [requestId, apiCall]);

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!detail || error) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error ?? 'Feedback not found.'}</p>
          <button onClick={() => navigate('/feedback')} className="text-pink-600 hover:underline text-sm">
            ← Back to Feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/feedback')}
          className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Inbox
        </button>

        {/* Header banner */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl px-8 py-6 mb-8 shadow-xl text-white">
          <div className="flex items-center gap-6 flex-wrap md:flex-nowrap">
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Inbox className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white leading-tight">Feedback Received</h1>
                <p className="text-white/80 text-sm mt-1">
                  From <span className="font-semibold text-white">{detail.recipientName}</span>
                </p>
              </div>
            </div>
            {detail.message && (
              <div className="bg-white/15 border border-white/20 rounded-xl px-4 py-3 md:ml-auto w-full md:max-w-md">
                <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">Your original note</p>
                <p className="text-sm text-white/90">{detail.message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Activity timeline */}
        <div className="mb-6">
          <ActivityTimeline events={detail.timeline} />
        </div>

        {/* Responses */}
        {detail.responses.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <p className="text-base font-medium">No responses yet</p>
          </div>
        ) : (
          <div className="space-y-5">
            {detail.responses.map((r, index) => (
              <div key={r.questionKey} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Question header strip */}
                <div className="bg-slate-100 border-b border-slate-200 px-6 py-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                    Question {index + 1}
                  </p>
                  <p className="text-sm font-semibold text-slate-700">{QUESTION_LABELS[r.questionKey] ?? r.questionText}</p>
                </div>
                {/* Answer */}
                <div className="px-6 py-5">
                  <div className="rich-text-editor text-slate-700 text-sm leading-relaxed" dangerouslySetInnerHTML={renderRichText(r.answer)} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pb-12">
          <button
            onClick={() => navigate('/feedback')}
            className="px-5 py-2.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 bg-white rounded-xl transition-colors"
          >
            ← Back to Inbox
          </button>
        </div>
      </main>
    </div>
  );
}
